import { debounce, isEqual, sortBy } from 'lodash'
import type { MultiSelectAllEvent, MultiSelectChangeEvent, MultiSelectFilterEvent } from 'primereact/multiselect'
import { MultiSelect } from 'primereact/multiselect'
import type { VirtualScrollerLazyEvent } from 'primereact/virtualscroller'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import './MultiSelectDropdown.scss'
import { isDropdownOption } from './multi-select-dropdown-util'

export interface DropdownOption {
  value: string
  text: string
}

export interface MultiSelectDropdownProps {
  options: Array<DropdownOption>
  values: Array<DropdownOption>
  placeholder?: string
  label?: string
  editable?: boolean
  fullWidth?: boolean
  required?: boolean
  error?: boolean
  useOptionAsValue?: boolean
  optionValueKey?: 'value' | 'text'
  //When the data is paged this needs to be used to check if all items are selected
  totalOptions?: number
  appendTo?: 'self' | HTMLElement
  onChange?: (selectedOptions: Array<DropdownOption>) => void
  onFilter?: (filter: string) => void
  onLazyLoad?: () => void
  onSelectAll?: () => void
}

export function MultiSelectDropdown({
  values,
  options,
  placeholder,
  label,
  fullWidth = false,
  editable = true,
  required = false,
  error = false,
  useOptionAsValue = true,
  optionValueKey = 'value',
  totalOptions,
  appendTo,
  onChange,
  onFilter,
  onLazyLoad,
  onSelectAll
}: MultiSelectDropdownProps) {
  const [selectedValues, setSelectedValues] = useState<Array<DropdownOption>>([])
  const [dropdownOptions, setDropdownOptions] = useState<Array<DropdownOption>>(options)
  const [isSelectAllSelected, setIsSelectAllSelected] = useState(false)
  const filterValueRef = useRef<string | undefined>(undefined)
  const id = useId()

  useEffect(() => {
    if (values && values.length > 0) {
      if (!isEqual(values, selectedValues)) {
        setSelectedValues(values)
        setDropdownOptions(groupSelectedValuesToTop(dropdownOptions, values))
      }
    } else if (!values || (values && values.length === 0)) {
      setSelectedValues([])
    }

    if ((!filterValueRef.current || filterValueRef.current.length === 0) && totalOptions && totalOptions > 0) {
      setIsSelectAllSelected(totalOptions === values.length)
    }
  }, [values, totalOptions])

  useEffect(() => {
    if (!isEqual(options, dropdownOptions)) {
      setDropdownOptions(groupSelectedValuesToTop(options))
    }

    //When select all is clicked and there is more than 50 options, the rest of them should also be selected
    if (options && options.length > 50 && isSelectAllSelected) {
      setSelectedValues(options)
    }
    //After the filter value is removed and the option list is updated, all values might not be selected anymore
    else if (
      !options.every(option => selectedValues.some(selected => selected.value === option.value)) &&
      isSelectAllSelected &&
      (!filterValueRef.current || (filterValueRef.current && filterValueRef.current.length === 0))
    ) {
      setIsSelectAllSelected(false)
    } else if (!isSelectAllSelected && values.length > 0 && options.length === values.length) {
      setIsSelectAllSelected(true)
    }
  }, [options])

  const debouncedOnChange = useCallback(
    debounce((newValues: Array<DropdownOption>) => {
      onChange?.(newValues)
    }, 300),
    [onChange]
  )

  const onDropdownValueChange = (event: MultiSelectChangeEvent) => {
    let newValues: Array<DropdownOption> = []

    if (useOptionAsValue) {
      // Runtime validate that event.value is an array of DropdownOption-like objects
      if (Array.isArray(event.value) && event.value.every(isDropdownOption)) {
        newValues = event.value
      } else if (Array.isArray(event.value)) {
        // Fallback: treat entries as ids and attempt to resolve
        const ids = event.value as Array<string>
        newValues = ids.map(id => options.find(o => o[optionValueKey] === id)).filter((o): o is DropdownOption => !!o)
      }
    } else {
      // event.value is an array of ids (string) referencing optionValueKey
      const currentIds = (event.value as Array<string>) || []
      const prevIds = selectedValues.map(v => v[optionValueKey])

      if (currentIds.length > prevIds.length) {
        // An id was added
        const addedId = currentIds.find(id => !prevIds.includes(id))
        const addedOption = options.find(o => o[optionValueKey] === addedId)
        newValues = addedOption ? [...selectedValues, addedOption] : selectedValues
      } else if (currentIds.length < prevIds.length) {
        // An id was removed
        const removedId = prevIds.find(id => !currentIds.includes(id))
        newValues = removedId ? selectedValues.filter(v => v[optionValueKey] !== removedId) : selectedValues
      } else {
        // Same length (could be reorder) just map ids to options
        newValues = currentIds
          .map(id => options.find(o => o[optionValueKey] === id))
          .filter((o): o is DropdownOption => !!o)
      }
    }

    setSelectedValues(newValues)
    debouncedOnChange(newValues)
    setIsSelectAllSelected(options.every(option => newValues.some(item => option.value === item.value)))
  }

  const onDropdownFilter = debounce((event: MultiSelectFilterEvent) => {
    filterValueRef.current = event.filter
    onFilter?.(event.filter)
  }, 250)

  const onLazyLoadScrollEvent = (event: VirtualScrollerLazyEvent) => {
    if (Number(event.last) > 0 && Number(event.last) % 50 === 0) {
      onLazyLoad?.()
    }
  }

  const onSelectAllValues = (event: MultiSelectAllEvent) => {
    setIsSelectAllSelected(!event.checked)

    //filter exists
    if (filterValueRef.current && filterValueRef.current.length > 0) {
      let values = []
      //remove values, that exists in the option list
      if (event.checked) {
        values = selectedValues.filter(item => !options.some(option => option[optionValueKey] === item[optionValueKey]))
      }
      //add values, that is not already selected
      else {
        const optionsNotSelected = options.filter(
          option => !selectedValues.some(item => option[optionValueKey] === item[optionValueKey])
        )
        values = [...selectedValues, ...optionsNotSelected]
      }

      setSelectedValues(values)
      onChange?.(values)
    }
    //no filters
    else {
      const newSelectedOptions = event.checked ? [] : options
      setSelectedValues(newSelectedOptions)
      if (event.checked) {
        onChange?.([])
      } else {
        onSelectAll?.()
      }
    }
  }

  const groupSelectedValuesToTop = (options: Array<DropdownOption>, selected?: Array<DropdownOption>) => {
    const values = selected ?? selectedValues

    return [
      ...sortBy(values, option => option.text.toLowerCase()),
      ...sortBy(
        options.filter(option => !values.some(selection => selection[optionValueKey] === option[optionValueKey])),
        option => option.text.toLowerCase()
      )
    ]
  }

  const onHidden = () => setDropdownOptions(groupSelectedValuesToTop(dropdownOptions))

  const renderSelectedItems = (value: string | DropdownOption | undefined) => {
    if (!value) {
      return <>{placeholder}</>
    }

    if (isDropdownOption(value)) {
      return <>{`${value.text} `}</>
    }

    return <>{`${value} `}</>
  }

  const dropdownValues = useOptionAsValue ? selectedValues : selectedValues.map(selected => selected[optionValueKey])

  const multiSelectClass = error ? 'p-invalid' : ''

  return (
    <div className='multi-select-dropdown' style={{ width: fullWidth ? '100%' : undefined }}>
      <MultiSelect
        value={dropdownValues}
        onChange={onDropdownValueChange}
        onFilter={onDropdownFilter}
        options={dropdownOptions}
        optionLabel='text'
        optionValue={optionValueKey}
        selectAll={isSelectAllSelected}
        onSelectAll={onSelectAllValues}
        virtualScrollerOptions={{
          itemSize: 32,
          lazy: true,
          onLazyLoad: onLazyLoadScrollEvent
        }}
        placeholder={placeholder}
        selectedItemTemplate={renderSelectedItems}
        maxSelectedLabels={3}
        disabled={!editable}
        inputId={id}
        filter
        filterPlaceholder='Type to search...'
        useOptionAsValue={useOptionAsValue}
        className={multiSelectClass}
        onHide={onHidden}
        appendTo={appendTo}
      />
      <label htmlFor={id} className={'multi-select-dropdown__label'}>
        {label}
        {required && <span className='required'>*</span>}
      </label>
    </div>
  )
}
